<script>
import { useEsignStore } from '../../stores/esign'
import { extractDeepErrorMessage } from '../../composables/useApiErrors'
import EcSpinner from '../ui/EcSpinner.vue'
import EcButton from '../ui/EcButton.vue'
import EcAlert from '../ui/EcAlert.vue'
import EcBadge from '../ui/EcBadge.vue'
import EcCollapse from '../ui/EcCollapse.vue'
import SignatureStatusBadge from './SignatureStatusBadge.vue'
import SignatureDocuments from './SignatureDocuments.vue'
import PackageEditor from './PackageEditor.vue'

var sendableStatuses = ['DRAFT', 'CREATED', 'DECLINED', 'EXPIRED']
var editableStatuses = ['DRAFT', 'CREATED']

var avatarColors = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
  '#10b981', '#06b6d4', '#6366f1', '#f43f5e',
]

export default {
  name: 'SignatureDetail',
  components: {
    EcSpinner,
    EcButton,
    EcAlert,
    EcBadge,
    EcCollapse,
    SignatureStatusBadge,
    SignatureDocuments,
    PackageEditor,
  },
  props: {
    signableType: {
      type: String,
      required: true,
    },
    signableId: {
      type: [String, Number],
      required: true,
    },
    signatureId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    var store = useEsignStore()
    return {
      store: store,
      signature: null,
      loading: true,
      sending: false,
      deleting: false,
      showDetails: false,
    }
  },
  computed: {
    t() {
      return this.$ecTranslate
    },
    canSend() {
      return this.signature && sendableStatuses.includes(this.signature.status)
    },
    canEdit() {
      return this.signature && editableStatuses.includes(this.signature.status)
    },
    canDelete() {
      return this.signature && sendableStatuses.includes(this.signature.status)
    },
    canDownload() {
      return this.signature && this.signature.status === 'COMPLETED'
    },
    editorUrl() {
      return (this.signature && this.signature.edition_url) || null
    },
    hasEditor() {
      return !!this.editorUrl
    },
    isResend() {
      return this.signature && ['DECLINED', 'EXPIRED'].includes(this.signature.status)
    },
    isFromTemplate() {
      return !!(this.signature && this.signature.model_id)
    },
    allSigners() {
      if (!this.signature) return []

      var sig = this.signature

      // Format 1: flat signers array (API response)
      if (sig.signers && sig.signers.length) {
        return sig.signers
          .filter(function (s) { return s.email })
          .map(function (s) {
            return {
              role: s.role || '',
              first_name: s.firstname || s.first_name || '',
              last_name: s.lastname || s.last_name || '',
              email: s.email || '',
              status: s.status || null,
            }
          })
      }

      // Format 2: nested roles array
      if (sig.roles && sig.roles.length) {
        return sig.roles.flatMap(function (role) {
          return (role.signers || []).map(function (signer) {
            return {
              role: role.name,
              first_name: signer.firstname || signer.first_name || '',
              last_name: signer.lastname || signer.last_name || '',
              email: signer.email || '',
              status: signer.status || null,
            }
          })
        })
      }

      return []
    },
    signedDocuments() {
      if (!this.signature) return []
      var meta = this.signature.meta
      if (meta && meta.signed_documents && meta.signed_documents.length) {
        return meta.signed_documents.map(function (doc) {
          return {
            name: doc.name || 'document',
            url: doc.url || '',
            ext: doc.ext || '',
          }
        })
      }
      // Fallback: signature.documents
      if (this.signature.documents && this.signature.documents.length) {
        return this.signature.documents
      }
      return []
    },
    infoItems() {
      if (!this.signature) return []
      var sig = this.signature
      var t = this.$ecTranslate
      var items = []

      if (this.isFromTemplate) {
        items.push({
          label: t('ELIXIO_CONNECT.TYPE'),
          value: t('ELIXIO_CONNECT.FROM_TEMPLATE'),
          icon: 'bi-file-earmark-text',
        })
      }

      items.push({
        label: t('ELIXIO_CONNECT.CREATED_AT'),
        value: this.formatDate(this.signature.created_at),
        icon: 'bi-calendar-plus',
      })

      if (sig.sent_at) {
        items.push({
          label: t('ELIXIO_CONNECT.SENT_AT'),
          value: this.formatDate(sig.sent_at),
          icon: 'bi-send',
        })
      }

      if (sig.completed_at) {
        items.push({
          label: t('ELIXIO_CONNECT.COMPLETED_AT'),
          value: this.formatDate(sig.completed_at),
          icon: 'bi-check-circle',
        })
      }

      items.push({
        label: t('ELIXIO_CONNECT.UPDATED_AT'),
        value: this.formatDate(this.signature.updated_at),
        icon: 'bi-clock-history',
      })

      return items
    },
  },
  mounted() {
    this.fetchSignature()
  },
  methods: {
    getAvatarColor(index) {
      return avatarColors[index % avatarColors.length]
    },
    getSignerStatusInfo(status) {
      var t = this.$ecTranslate
      if (!status) return { label: '', icon: '', variant: 'secondary' }

      var upper = status.toUpperCase()
      if (upper === 'SIGNING_COMPLETE' || upper === 'COMPLETED') {
        return { label: t('ELIXIO_CONNECT.SIGNER_STATUS_SIGNED'), icon: 'bi-check-circle-fill', variant: 'success' }
      }
      if (upper === 'SIGNING_PENDING' || upper === 'PENDING') {
        return { label: t('ELIXIO_CONNECT.SIGNER_STATUS_PENDING'), icon: 'bi-hourglass-split', variant: 'warning' }
      }
      if (upper === 'DECLINED') {
        return { label: t('ELIXIO_CONNECT.SIGNER_STATUS_DECLINED'), icon: 'bi-x-circle-fill', variant: 'danger' }
      }
      return { label: status, icon: 'bi-question-circle', variant: 'secondary' }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString()
    },
    async fetchSignature() {
      this.loading = true
      try {
        var result = await this.store.getSignature(this.signableType, this.signableId, {
          Id: this.signatureId,
          filter: { include_edition_url: 1 },
        })
        if (Array.isArray(result)) {
          var self = this
          this.signature =
            result.find(function (s) { return String(s.id) === String(self.signatureId) }) || result[0] || null
        } else if (result && typeof result === 'object' && result.id) {
          this.signature = result
        } else {
          this.signature = null
        }

        if (this.signature && this.signature.edition_url) {
          this.$emit('request-fullscreen')
        }
      } finally {
        this.loading = false
      }
    },
    async handleSend() {
      if (!this.signature) return

      var t = this.$ecTranslate
      var confirmed = this.$ecConfirm
        ? await this.$ecConfirm(t('ELIXIO_CONNECT.SEND') + ' "' + this.signature.name + '" ' + t('ELIXIO_CONNECT.FOR_SIGNATURE') + ' ?')
        : window.confirm(t('ELIXIO_CONNECT.SEND') + ' "' + this.signature.name + '" ' + t('ELIXIO_CONNECT.FOR_SIGNATURE') + ' ?')
      if (!confirmed) return

      this.sending = true
      try {
        await this.store.sendSignature(this.signableType, this.signableId, this.signature.id)
        this.$ecNotify(t('ELIXIO_CONNECT.SIGNATURE_SENT'), 'success')
        await this.fetchSignature()
        this.$emit('send', this.signature)
      } catch (err) {
        this.$ecNotify(extractDeepErrorMessage(err, t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')), 'error')
      } finally {
        this.sending = false
      }
    },
    handleEdit() {
      if (!this.signature) return
      this.$emit('edit', this.signature)
    },
    async handleDelete() {
      if (!this.signature) return

      var t = this.$ecTranslate
      var confirmed = this.$ecConfirm
        ? await this.$ecConfirm(t('ELIXIO_CONNECT.DELETE') + ' "' + this.signature.name + '" ?')
        : window.confirm(t('ELIXIO_CONNECT.DELETE') + ' "' + this.signature.name + '" ?')
      if (!confirmed) return

      this.deleting = true
      try {
        await this.store.deleteSignature(this.signableType, this.signableId, this.signature.id)
        this.$ecNotify(t('ELIXIO_CONNECT.SIGNATURE_DELETED'), 'success')
        this.$emit('delete', this.signature)
      } catch (err) {
        this.$ecNotify(extractDeepErrorMessage(err, t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')), 'error')
      } finally {
        this.deleting = false
      }
    },
    async handleDownload() {
      if (!this.signature) return
      await this.store.downloadDocuments(this.signableType, this.signableId, this.signature.id)
    },
  },
}
</script>

<template>
  <div class="ec-signature-detail">
    <EcSpinner v-if="loading" />

    <template v-else-if="signature">
      <!-- Header -->
      <div class="ec-sig-header">
        <h4 class="ec-sig-header__title">{{ signature.name }}</h4>
        <div class="ec-sig-header__bar">
          <div class="ec-sig-header__meta">
            <span v-if="signature.code" class="ec-sig-header__code">{{ signature.code }}</span>
            <SignatureStatusBadge :status="signature.status" />
          </div>
          <div class="ec-sig-header__actions">
            <EcButton v-if="canSend" variant="primary" icon="send" size="sm" :disabled="sending" @click="handleSend">
              <EcSpinner v-if="sending" size="sm" style="margin-right: 0.25rem" />
              {{ isResend ? t('ELIXIO_CONNECT.RESEND') : t('ELIXIO_CONNECT.SEND') }}
            </EcButton>
            <EcButton v-if="canEdit" variant="outline-secondary" icon="pencil" size="sm" @click="handleEdit">
              {{ t('ELIXIO_CONNECT.EDIT') }}
            </EcButton>
            <EcButton v-if="canDelete" variant="outline-danger" icon="trash" size="sm" :disabled="deleting" @click="handleDelete">
              <EcSpinner v-if="deleting" size="sm" style="margin-right: 0.25rem" />
              {{ t('ELIXIO_CONNECT.DELETE') }}
            </EcButton>
            <EcButton v-if="canDownload && !signedDocuments.length" variant="success" icon="download" size="sm" @click="handleDownload">
              {{ t('ELIXIO_CONNECT.GET_DOCUMENTS') }}
            </EcButton>
          </div>
        </div>
      </div>

      <!-- Content: collapsible when editor present, always visible otherwise -->
      <EcCollapse v-if="hasEditor" :value="showDetails" @input="showDetails = $event">
        <template #trigger>
          <span class="ec-sig-collapse-trigger">
            <i class="bi bi-info-circle" />
            {{ t('ELIXIO_CONNECT.DETAILS') }}
          </span>
        </template>
        <div class="ec-sig-content">
          <!-- Info grid -->
          <div class="ec-sig-info-grid">
            <div v-for="item in infoItems" :key="item.label" class="ec-sig-info-item">
              <span class="ec-sig-info-item__label">
                <i class="bi" :class="item.icon" />
                {{ item.label }}
              </span>
              <span class="ec-sig-info-item__value">{{ item.value }}</span>
            </div>
          </div>

          <!-- Description & Email message side by side -->
          <div v-if="signature.description || signature.email_message" class="ec-sig-blocks-row">
            <div v-if="signature.description" class="ec-sig-block">
              <div class="ec-sig-block__header">
                <i class="bi bi-text-left" />
                <span>{{ t('ELIXIO_CONNECT.EXCERPT') }}</span>
              </div>
              <p class="ec-sig-block__text">{{ signature.description }}</p>
            </div>
            <div v-if="signature.email_message" class="ec-sig-block">
              <div class="ec-sig-block__header">
                <i class="bi bi-envelope-open" />
                <span>{{ t('ELIXIO_CONNECT.EMAIL_MESSAGE') }}</span>
              </div>
              <p class="ec-sig-block__text">{{ signature.email_message }}</p>
            </div>
          </div>

          <!-- Signers -->
          <div class="ec-sig-section">
            <h6 class="ec-sig-section__title">
              <i class="bi bi-people" />
              {{ t('ELIXIO_CONNECT.SIGNERS') }}
              <span v-if="allSigners.length" class="ec-sig-section__count">{{ allSigners.length }}</span>
            </h6>
            <div v-if="allSigners.length" class="ec-sig-signers">
              <div v-for="(signer, idx) in allSigners" :key="idx" class="ec-sig-signer">
                <div class="ec-sig-signer__avatar" :style="{ background: getAvatarColor(idx) }">
                  {{ ((signer.first_name || '')[0] || '').toUpperCase() }}{{ ((signer.last_name || '')[0] || '').toUpperCase() }}
                </div>
                <div class="ec-sig-signer__info">
                  <div class="ec-sig-signer__name">
                    {{ signer.first_name }} {{ signer.last_name }}
                    <EcBadge v-if="signer.role" variant="primary" size="xs">{{ signer.role }}</EcBadge>
                  </div>
                  <div class="ec-sig-signer__email">
                    <i class="bi bi-envelope" />
                    {{ signer.email }}
                  </div>
                </div>
                <span
                  v-if="signer.status"
                  class="ec-sig-signer__status"
                  :class="'ec-sig-signer__status--' + getSignerStatusInfo(signer.status).variant"
                >
                  <i class="bi" :class="getSignerStatusInfo(signer.status).icon" />
                  {{ getSignerStatusInfo(signer.status).label }}
                </span>
              </div>
            </div>
            <p v-else class="ec-text-muted" style="font-size: 0.8125rem">
              {{ t('ELIXIO_CONNECT.NO_SIGNERS') }}
            </p>
          </div>
        </div>
      </EcCollapse>

      <!-- Editor area -->
      <div v-if="hasEditor" class="ec-sig-editor">
        <PackageEditor :editor-url="editorUrl" height="100%" />
      </div>

      <!-- Simple layout (no editor) — details always visible -->
      <template v-if="!hasEditor">
        <div class="ec-sig-content">
          <!-- Info grid -->
          <div class="ec-sig-info-grid">
            <div v-for="item in infoItems" :key="item.label" class="ec-sig-info-item">
              <span class="ec-sig-info-item__label">
                <i class="bi" :class="item.icon" />
                {{ item.label }}
              </span>
              <span class="ec-sig-info-item__value">{{ item.value }}</span>
            </div>
          </div>

          <!-- Description & Email message side by side -->
          <div v-if="signature.description || signature.email_message" class="ec-sig-blocks-row">
            <div v-if="signature.description" class="ec-sig-block">
              <div class="ec-sig-block__header">
                <i class="bi bi-text-left" />
                <span>{{ t('ELIXIO_CONNECT.EXCERPT') }}</span>
              </div>
              <p class="ec-sig-block__text">{{ signature.description }}</p>
            </div>
            <div v-if="signature.email_message" class="ec-sig-block">
              <div class="ec-sig-block__header">
                <i class="bi bi-envelope-open" />
                <span>{{ t('ELIXIO_CONNECT.EMAIL_MESSAGE') }}</span>
              </div>
              <p class="ec-sig-block__text">{{ signature.email_message }}</p>
            </div>
          </div>

          <!-- Signers -->
          <div class="ec-sig-section">
            <h6 class="ec-sig-section__title">
              <i class="bi bi-people" />
              {{ t('ELIXIO_CONNECT.SIGNERS') }}
              <span v-if="allSigners.length" class="ec-sig-section__count">{{ allSigners.length }}</span>
            </h6>
            <div v-if="allSigners.length" class="ec-sig-signers">
              <div v-for="(signer, idx) in allSigners" :key="idx" class="ec-sig-signer">
                <div class="ec-sig-signer__avatar" :style="{ background: getAvatarColor(idx) }">
                  {{ ((signer.first_name || '')[0] || '').toUpperCase() }}{{ ((signer.last_name || '')[0] || '').toUpperCase() }}
                </div>
                <div class="ec-sig-signer__info">
                  <div class="ec-sig-signer__name">
                    {{ signer.first_name }} {{ signer.last_name }}
                    <EcBadge v-if="signer.role" variant="primary" size="xs">{{ signer.role }}</EcBadge>
                  </div>
                  <div class="ec-sig-signer__email">
                    <i class="bi bi-envelope" />
                    {{ signer.email }}
                  </div>
                </div>
                <span
                  v-if="signer.status"
                  class="ec-sig-signer__status"
                  :class="'ec-sig-signer__status--' + getSignerStatusInfo(signer.status).variant"
                >
                  <i class="bi" :class="getSignerStatusInfo(signer.status).icon" />
                  {{ getSignerStatusInfo(signer.status).label }}
                </span>
              </div>
            </div>
            <p v-else class="ec-text-muted" style="font-size: 0.8125rem">
              {{ t('ELIXIO_CONNECT.NO_SIGNERS') }}
            </p>
          </div>

          <!-- Signed Documents -->
          <div v-if="canDownload" class="ec-sig-section">
            <h6 class="ec-sig-section__title">
              <i class="bi bi-file-earmark-check" />
              {{ t('ELIXIO_CONNECT.SIGNED_FILES') }}
            </h6>
            <SignatureDocuments :documents="signedDocuments" />
          </div>
        </div>
      </template>
    </template>

    <EcAlert v-else type="warning">
      {{ t('ELIXIO_CONNECT.SIGNATURE_NOT_FOUND') }}
    </EcAlert>
  </div>
</template>

<style scoped>
.ec-signature-detail {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  height: 100%;
}

/* Header */
.ec-sig-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--ec-border-color, #e2e8f0);
}

.ec-sig-header__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--ec-text-color, #1f2937);
  line-height: 1.3;
}

.ec-sig-header__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.ec-sig-header__meta {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
}

.ec-sig-header__code {
  font-weight: 700;
  font-size: 0.75rem;
  font-family: monospace;
  color: var(--ec-text-muted, #6b7280);
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.ec-sig-header__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  flex-shrink: 0;
}

/* Editor */
.ec-sig-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Content wrapper */
.ec-sig-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1rem;
}

/* Info grid */
.ec-sig-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0;
  background: var(--ec-bg-muted, #f8f9fa);
  border-radius: 10px;
  border: 1px solid var(--ec-border-color, #e2e8f0);
  overflow: hidden;
}

.ec-sig-info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--ec-border-color, #e2e8f0);
}

.ec-sig-info-item:last-child {
  border-bottom: none;
}

.ec-sig-info-item__label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ec-text-muted, #6b7280);
}

.ec-sig-info-item__label i {
  font-size: 0.75rem;
}

.ec-sig-info-item__value {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--ec-text-color, #1f2937);
  line-height: 1.4;
}

/* Blocks row */
.ec-sig-blocks-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

/* Info blocks */
.ec-sig-block {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.875rem 1rem;
  background: var(--ec-bg-muted, #f8f9fa);
  border-radius: 10px;
  border: 1px solid var(--ec-border-color, #e2e8f0);
}

.ec-sig-block--message {
  border-left: 3px solid var(--ec-primary, #3b82f6);
}

.ec-sig-block__header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ec-text-muted, #6b7280);
}

.ec-sig-block__header i {
  font-size: 0.8125rem;
}

.ec-sig-block__text {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--ec-text-color, #1f2937);
  line-height: 1.5;
  white-space: pre-line;
}

/* Sections */
.ec-sig-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ec-sig-section__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ec-text-muted, #6b7280);
}

.ec-sig-section__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  border-radius: 10px;
  background: var(--ec-border-color, #e2e8f0);
  color: var(--ec-text-muted, #6b7280);
  font-size: 0.6875rem;
  font-weight: 700;
}

/* Signer cards */
.ec-sig-signers {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.625rem;
}

.ec-sig-signer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  background: var(--ec-bg, #fff);
  border: 1px solid var(--ec-border-color, #e2e8f0);
  border-radius: 10px;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.ec-sig-signer:hover {
  border-color: var(--ec-primary, #3b82f6);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.ec-sig-signer__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.ec-sig-signer__info {
  flex: 1;
  min-width: 0;
}

.ec-sig-signer__name {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--ec-text-color, #1f2937);
  line-height: 1.3;
}

.ec-sig-signer__email {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--ec-text-muted, #6b7280);
  margin-top: 0.125rem;
}

.ec-sig-signer__email i {
  font-size: 0.625rem;
}

.ec-sig-signer__status {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 20px;
  white-space: nowrap;
}

.ec-sig-signer__status--success {
  color: #15803d;
  background: #dcfce7;
}

.ec-sig-signer__status--warning {
  color: #a16207;
  background: #fef9c3;
}

.ec-sig-signer__status--danger {
  color: #dc2626;
  background: #fee2e2;
}

.ec-sig-signer__status--secondary {
  color: var(--ec-text-muted, #6b7280);
  background: var(--ec-bg-muted, #f3f4f6);
}

/* Collapse trigger */
.ec-sig-collapse-trigger {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--ec-text-muted, #6b7280);
}
</style>
